import { useMyLocation } from '@/services/useLocations';
import { useCallback } from 'react';
import { useTimeSlots } from '../../../components/gantt/gantt/ganttHelper';
import { useSystemSettings } from '../../../services/settings/SystemSettingsProvider';
import { useTasks } from '../../../services/tasks/useTasks';
import { WorkingShift } from '../../../types/daily.types';

export const useBuildHourSummary = (includeUnassigned?: boolean) => {
  const includeBreaks = useSystemSettings(state => state.settings.includeBreaksInTotals);
  const tasks = useTasks();
  const { id: selectedLocationId } = useMyLocation();

  return useCallback(
    (shifts: WorkingShift[], start: number, end: number) => {
      const assignedShifts = shifts.filter(
        (shift: any) => shift.assignedEmployeeId && shift.assignedStaffHomeLocationId === selectedLocationId
      );
      const shiftsToUse = includeUnassigned ? shifts : assignedShifts;
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const hourlySlots = useTimeSlots(
        Math.floor(start / (60 * 60)) * 60 * 60,
        Math.ceil(end / (60 * 60)) * 60 * 60,
        60 * 60
      );

      return hourlySlots.map(slot => {
        return shiftsToUse.reduce((prevTotal, shift) => {
          // Shift times not defined.
          if (!shift.start || !shift.end) {
            return prevTotal;
          }

          // Shift doesn't cover half hour slot.
          if (shift.start >= slot.end || shift.end <= slot.start) {
            return prevTotal;
          }

          let timeToCount = 60 * 60;

          if (slot.start < shift.start && shift.start < slot.end) {
            // Shift starts part way through the slot- only need to add the last bit of the slot.
            timeToCount = slot.end - shift.start;
          }

          if (slot.start < shift.end && shift.end < slot.end) {
            // Shift ends part way through the slot- only need to add the first bit of the slot.
            timeToCount = shift.end - slot.start;
          }

          if (!includeBreaks) {
            // Shift overlaps a break.
            shift.breaks.forEach(brek => {
              if (brek.start <= slot.start && slot.end <= brek.end) {
                // Break completely overlaps slot- don't need to add anything to the total.
                timeToCount = 0;
              } else if (slot.start < brek.start && brek.start < slot.end) {
                // The start of the break lies in the slot, so only add the bit before the break start.
                timeToCount = brek.start - slot.start;
              } else if (slot.start < brek.end && brek.end < slot.end) {
                // The end of the break lies in the slot, so only add the bit after the break ends.
                timeToCount = slot.end - brek.end;
              }
            });
          }

          // find the task that fits the slot
          if (shift.tasks) {
            // find all tasks that overlap the slot
            const tasksInSlot = shift.tasks.filter(task => {
              return task.start < slot.end && task.end > slot.start;
            });

            // for each tasksInSlot, find from tasks that isInCalculatedWorkingHours is false
            const tasksToCount = tasksInSlot.filter(task => {
              return tasks && tasks.find(t => t.id === task.typeContext && !t.isInCalculatedWorkingHours);
            });

            // get the total time of the tasksToCount
            const taskTime = tasksToCount.reduce((prev, task) => {
              const taskStart = Math.max(task.start, slot.start);
              const taskEnd = Math.min(task.end, slot.end);

              return prev + (taskEnd - taskStart);
            }, 0);

            // add the task time to the minutesToCount
            timeToCount -= taskTime;
          }

          return prevTotal + timeToCount;
        }, 0);
      });
    },
    [includeBreaks, includeUnassigned, selectedLocationId, tasks]
  );
};

export const useHourSummary = (shifts: WorkingShift[], start: number, end: number, includeUnassigned?: boolean) => {
  const buildHourSummary = useBuildHourSummary(includeUnassigned);

  return buildHourSummary(shifts, start, end);
};
